import { useQuery } from 'react-query';

import { type JsonDataItem } from 'types';
import { parseErrors, Toast } from 'helpers';
import { getUserPerformanceStats } from 'queries';

const defaults: Awaited<ReturnType<typeof getUserPerformanceStats>> = {
  totalCalls: 0,
  avgCallDuration: 0,
  loginTime: 0,
  totalJobs: 0,
  sameDayJobs: 0,
};

const SECONDS_PER_HOUR = 60 * 60; // 60 seconds per minute, 60 minutes per hour
const SECONDS_PER_MINUTE = 60; // 60 seconds per minute

function usePerformanceStats() {
  const { data, isLoading } = useQuery('user-performance-stats', getUserPerformanceStats, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { totalCalls, avgCallDuration, loginTime, totalJobs, sameDayJobs } = data || defaults;
  const loginTimeInHours = loginTime / SECONDS_PER_HOUR;

  const jobsPerHour = loginTimeInHours ? totalJobs / loginTimeInHours : 0;
  const callsPerHour = loginTimeInHours ? totalCalls / loginTimeInHours : 0;
  const conversionRate = totalCalls ? totalJobs / totalCalls : 0;
  const sdsStrikeRate = totalJobs ? sameDayJobs / totalJobs : 0;

  const stats = {
    jobsPerHour: jobsPerHour.toFixed(2),
    callsPerHour: callsPerHour.toFixed(2),
    avgCallDuration: (avgCallDuration / SECONDS_PER_MINUTE).toFixed(2),
    conversionRate: `${(conversionRate * 100).toFixed(0)}%`,
    sdsStrikeRate: `${(sdsStrikeRate * 100).toFixed(0)}%`,
  };

  return [stats, isLoading] as [data: typeof stats, isLoading: boolean];
}

export default usePerformanceStats;
